import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  hasBlueprintSession,
  getAllBlueprints,
  getBlueprintById as apiGetBlueprintById,
  getBlueprintByRoute,
  recordBlueprintView,
} from '../services/blueprintsApi';

const BlueprintContext = createContext(null);

/**
 * 2026-06-21 (FIX 8 item 1) — CAUSE OF THE DASHBOARD 401.
 * BlueprintProvider wraps the WHOLE app, including the public /login route, and
 * its mount effect fetched the blueprints list unconditionally. The axios
 * interceptor attaches the bearer token from localStorage per request, so the
 * header was never the problem — there was simply no session yet, which is why
 * it 401'd exactly once per load.
 *
 * 2026-06-22 (FIX 9) — the session gate, the in-flight/dedupe guard and the
 * configured axios client (with its token-refresh queue) now live in
 * services/blueprintsApi.js, shared with pages/BlueprintLibrary and
 * pages/GuidePage. Three copies of this fetch were the cause of the duplicate
 * requests per load.
 */
export const useBlueprint = () => {
  const context = useContext(BlueprintContext);
  if (!context) {
    throw new Error('useBlueprint must be used within a BlueprintProvider');
  }
  return context;
};

export const BlueprintProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentBlueprint, setCurrentBlueprint] = useState(null);
  const [allBlueprints, setAllBlueprints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null }); // null = centered
  const [currentRoute, setCurrentRoute] = useState('');
  const [routeBlueprint, setRouteBlueprint] = useState(null); // Blueprint for current route
  const [routeChanged, setRouteChanged] = useState(false); // Flag when route changes
  const [showIndicators, setShowIndicators] = useState(false); // Show/hide block indicators
  const [activeBlockSection, setActiveBlockSection] = useState(null); // Currently selected block section
  const allBlueprintsRef = useRef([]);
  useEffect(() => { allBlueprintsRef.current = allBlueprints; }, [allBlueprints]);

  const fetchAllBlueprints = useCallback(async () => {
    try {
      const list = await getAllBlueprints();
      setAllBlueprints(list);
    } catch (error) {
      console.error('Error fetching blueprints:', error);
    }
  }, []);

  // Fetch all blueprints on mount — only once signed in (gate lives in the service).
  useEffect(() => {
    if (!hasBlueprintSession()) return;
    fetchAllBlueprints();
  }, [fetchAllBlueprints]);

  // Listen for route changes from BlueprintRouteTracker
  useEffect(() => {
    const handleRouteChange = async (event) => {
      const newRoute = event.detail.route;
      setCurrentRoute(newRoute);

      if (!hasBlueprintSession()) {
        setRouteBlueprint(null);
        return;
      }
      // First navigation after login is also the retry for the mount fetch.
      // The service dedupes, so this cannot double up on an existing request.
      if (allBlueprintsRef.current.length === 0) fetchAllBlueprints();

      // Always fetch the blueprint for this route
      try {
        const blueprint = await getBlueprintByRoute(newRoute);
        if (blueprint) {
          setRouteBlueprint(blueprint);
          // If viewer is open, auto-update the current blueprint
          if (isOpen) {
            setCurrentBlueprint(blueprint);
            recordBlueprintView(blueprint.id);
          }
        } else {
          setRouteBlueprint(null);
        }
      } catch (error) {
        console.error('Error fetching route blueprint:', error);
        setRouteBlueprint(null);
      }
    };

    window.addEventListener('blueprint-route-change', handleRouteChange);
    return () => window.removeEventListener('blueprint-route-change', handleRouteChange);
  }, [isOpen, fetchAllBlueprints]);

  const fetchBlueprintByRoute = useCallback(async (route) => {
    if (!route) return;
    setLoading(true);
    try {
      const blueprint = await getBlueprintByRoute(route);
      if (blueprint) {
        setCurrentBlueprint(blueprint);
        setRouteBlueprint(blueprint);
        setRouteChanged(false);
        recordBlueprintView(blueprint.id);
      }
    } catch (error) {
      console.error('Error fetching blueprint by route:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBlueprintById = useCallback(async (id) => {
    setLoading(true);
    try {
      const blueprint = await apiGetBlueprintById(id);
      if (blueprint) {
        setCurrentBlueprint(blueprint);
        setRouteChanged(currentRoute && blueprint.id !== routeBlueprint?.id);
        recordBlueprintView(id);
      }
    } catch (error) {
      console.error('Error fetching blueprint:', error);
    } finally {
      setLoading(false);
    }
  }, [currentRoute, routeBlueprint]);

  const loadPageBlueprint = useCallback(() => {
    if (routeBlueprint) {
      setCurrentBlueprint(routeBlueprint);
      setRouteChanged(false);
      recordBlueprintView(routeBlueprint.id);
    }
  }, [routeBlueprint]);

  const toggleBlueprint = useCallback(async () => {
    if (!isOpen) {
      // Opening - fetch and load the route blueprint
      setIsOpen(true);
      setShowIndicators(true); // Show block indicators when blueprint is open
      setPosition({ x: null, y: null });
      setActiveBlockSection(null); // Reset active section

      // Get the current route from window.location if not set
      const route = currentRoute || (window.location.pathname + window.location.search);

      // If we already have a routeBlueprint for this route, use it
      if (routeBlueprint) {
        setCurrentBlueprint(routeBlueprint);
        recordBlueprintView(routeBlueprint.id);
      } else if (route) {
        // Otherwise fetch it
        try {
          const blueprint = await getBlueprintByRoute(route);
          if (blueprint) {
            setCurrentBlueprint(blueprint);
            setRouteBlueprint(blueprint);
            setCurrentRoute(route);
            recordBlueprintView(blueprint.id);
          }
        } catch (error) {
          console.error('Error fetching blueprint:', error);
        }
      }
    } else {
      setIsOpen(false);
      setShowIndicators(false); // Hide indicators when closed
      setActiveBlockSection(null);
    }
  }, [isOpen, routeBlueprint, currentRoute]);

  const openBlueprint = useCallback(async (blueprintId = null, preserveSection = false) => {
    setIsOpen(true);
    setShowIndicators(true);
    setPosition({ x: null, y: null }); // Center on open

    if (blueprintId) {
      if (!preserveSection) setActiveBlockSection(null);
      fetchBlueprintById(blueprintId);
    } else if (routeBlueprint) {
      setCurrentBlueprint(routeBlueprint);
      setRouteChanged(false);
    } else {
      // No routeBlueprint cached, fetch it
      const route = window.location.pathname + window.location.search;
      try {
        const blueprint = await getBlueprintByRoute(route);
        if (blueprint) {
          setCurrentBlueprint(blueprint);
          setRouteBlueprint(blueprint);
          setCurrentRoute(route);
          recordBlueprintView(blueprint.id);
        }
      } catch (error) {
        console.error('Error fetching blueprint:', error);
      }
    }
  }, [routeBlueprint, fetchBlueprintById]);

  const closeBlueprint = useCallback(() => {
    setIsOpen(false);
    setShowIndicators(false);
    setActiveBlockSection(null);
  }, []);

  const toggleIndicators = useCallback(() => {
    setShowIndicators(prev => !prev);
  }, []);

  // Get current active block section content
  const getActiveBlockContent = useCallback(() => {
    if (!activeBlockSection || !currentBlueprint?.content?.blockSections) {
      return null;
    }
    return currentBlueprint.content.blockSections.find(s => s.id === activeBlockSection);
  }, [activeBlockSection, currentBlueprint]);

  const updatePosition = useCallback((newPosition) => {
    setPosition(newPosition);
  }, []);

  const value = {
    isOpen,
    currentBlueprint,
    allBlueprints,
    loading,
    position,
    currentRoute,
    routeBlueprint,
    routeChanged,
    showIndicators,
    activeBlockSection,
    toggleBlueprint,
    openBlueprint,
    closeBlueprint,
    updatePosition,
    fetchBlueprintById,
    fetchBlueprintByRoute,
    fetchAllBlueprints,
    setCurrentBlueprint,
    loadPageBlueprint,
    toggleIndicators,
    setActiveBlockSection,
    getActiveBlockContent,
  };

  return (
    <BlueprintContext.Provider value={value}>
      {children}
    </BlueprintContext.Provider>
  );
};

export default BlueprintContext;
